import React, { useState, useEffect, useRef } from "react";
import {
    Box, Button, TextField, Typography, Grid, FormControl,
    FormControlLabel,
    Switch, Snackbar,
    Alert,
    AlertProps, InputAdornment, Dialog,
    Card, CardContent, Toolbar, DialogContent, DialogActions
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { IconButton, Tooltip, Paper } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth, useLoading, useNavigation, useSnackbar } from "../../../Context/Contexts";
import { SalesLinesProps, SalesLineViewModel, SalesLineDocumentResultModel, PaymentLineDocumentResultModel, ReturnViewModel, SalesDocumentViewModel, Option, Payment, ReturnModeRequest, ListQuery }
    from "../../../Types/ExportTypes";
import AsyncAutoComplete from "../../../components/AsyncAutoComplete";
import ShippingAgentServiceSelect from "../../../components/Shipping/ShippingAgentServiceSelect";
import {
    LocationAutoComplete,
    CountryAutoComplete,
    ReturnReasonAutoComplete,
} from "../../../components/AutoComplete/AutoCompletes";
import SalesDocumentActions from "../../../components/SalesDocument/SalesDocumentActions";
import SalesDocumentHeader from "../../../components/SalesDocument/SalesDocumentHeader";
import InfoButton from "../../../components/Tooltip/InfoTextField";
import CenteredCard from "../../../components/Card/CenteredCardLayout";
import { ToolbarWithSaveHandle } from "../../../components/Toolbar/ToolbarWithSave";
import { generateVariantLabel, generateVariantLabelFromReturnMode } from '../../../Utils/VariantUtils';
import ImageBox from "../../../components/Image/ImageBox";

import LocalizedNumberInputs from "../../../components/Input/LocalizedNumberInputs"
import { formatNumber } from '../../../Utils/NumberUtils';
import ResponsivePagination from "../../../components/ResponsivePagination";
const ReturnEditPage: React.FC<{ params?: { [key: string]: any } }> = ({ params }) => {
    const { returnNo = "" } = params || {};
    const history: boolean = String(params?.history) == "true";
    const { authService } = useAuth();
    const { goBack, navigate } = useNavigation();
    const { showSnackbar } = useSnackbar();
    const { showLoading, hideLoading, isLoading } = useLoading();
    const [loadedOptions, setloadedOptions] = useState(false);
    const { t } = useTranslation();

    const [returnDocument, setReturnDocument] = useState<ReturnViewModel | null>(null);
    const [originalPayments, setOriginalPayments] = useState<Payment[]>([]);
    const [paymentQuery, setPaymentQuery] = useState<ListQuery>({ page: 0, pageSize: 2, filter: "" });
    const [paymentTotalCount, setPaymentTotalCount] = useState(0);
    const [refundPayments, setRefundPayments] = useState<Payment[]>([]);
    const [refundPaymentQuery, setRefundPaymentQuery] = useState<ListQuery>({ page: 0, pageSize: 2, filter: "" });
    const [refundPaymentTotalCount, setRefundPaymentTotalCount] = useState(0);
    //const [isLoading, setIsLoading] = useState(false);

    const [showShippingInfo, setShowShippingInfo] = useState(false);
    const [showInvoicePayments, setShowInvoicePayments] = useState(false);
    const [showReplacement, setShowReplacement] = useState(false);
    const [replacementOrder, setReplacementOrder] = useState<SalesDocumentViewModel | null>(null);
    const [replacementDialogOpen, setReplacementDialogOpen] = useState(false);
    const [invoiceLines, setInvoiceLines] = useState<SalesLineViewModel[]>([]);
    const [invoiceQuery, setInvoiceQuery] = useState<ListQuery>({ page: 0, pageSize: 5, filter: "" });
    const [invoiceTotalCount, setInvoiceTotalCount] = useState(0);
    const [creditLines, setCreditLines] = useState<SalesLineViewModel[]>([]);
    const [creditQuery, setCreditQuery] = useState<ListQuery>({ page: 0, pageSize: 5, filter: "" });
    const [creditTotalCount, setCreditTotalCount] = useState(0);
    const [invoiceRowMode, setInvoiceRowMode] = useState(true);
    const [creditRowMode, setCreditRowMode] = useState(true);
    const [editingInvoiceLine, setEditingInvoiceLine] = useState<ReturnModeRequest | null>(null);
    const [editingCreditLine, setEditingCreditLine] = useState<SalesLineViewModel | null>(null);
    const toolbarRef = useRef<ToolbarWithSaveHandle>(null);
    const [initialLoad, setInitialLoad] = useState(true);
    const hasPendingChanges = () => returnDocument?.hasChanges === true;

    const showHasChangesError = () => {
        toolbarRef.current?.showHasChangesError();
    };

    const preventIfUnsaved = () => {
        if (hasPendingChanges()) {
            showHasChangesError();
            return true;
        }
        return false;
    };

    useEffect(() => {
        if (returnNo) fetchReturn(returnNo);

    }, [returnNo]);


    useEffect(() => {
        if (returnDocument?.invoiceNo) {
            fetchInvoiceLines(returnDocument.invoiceNo, invoiceQuery);
        }
    }, [invoiceQuery, returnDocument?.invoiceNo]);

    useEffect(() => {
        if (returnDocument?.returnNo) {
            fetchCreditLines(returnDocument.returnNo, creditQuery);
        }
    }, [creditQuery, returnDocument?.returnNo]);

    useEffect(() => {
        if (returnDocument?.invoiceNo) {
            fetchPayments(returnDocument.invoiceNo, paymentQuery);
        }
    }, [paymentQuery, returnDocument?.invoiceNo]);

    useEffect(() => {
        if (returnDocument?.returnNo) {
            fetchRefundPayments(returnDocument.returnNo, refundPaymentQuery);
        }
    }, [refundPaymentQuery, returnDocument?.returnNo]);

    useEffect(() => {
        if (showReplacement) {
            fetchReplacementOrder();
        }
    }, [showReplacement, returnDocument?.returnNo]);


    const toggleInvoiceRowMode = () => {
        const newMode = !invoiceRowMode;
        setInvoiceRowMode(newMode);
        setInvoiceQuery(prev => ({ ...prev, page: 0, pageSize: newMode ? 20 : 2 }));
    };

    const toggleCreditRowMode = () => {
        const newMode = !creditRowMode;
        setCreditRowMode(newMode);
        setCreditQuery(prev => ({ ...prev, page: 0, pageSize: newMode ? 20 : 2 }));
    };

    const handleSave = async () => {
        showLoading();
        try {
            var response = await authService.post<ReturnViewModel>("", returnDocument);
            setReturnDocument(
                response
                    ? { ...response, originalValues: { ...response }, hasChanges: false }
                    : null
            );
            showSnackbar(t("common.documentSaved"), "success");
        } catch (error: any) {
            showSnackbar(`${t("common.failedToSaveDocument")} : ${error.response?.data ?? error.message}`, "error");
        } finally {
            hideLoading();
        }
    };

    const handlePostReturn = async () => {
        if (preventIfUnsaved() || !returnDocument) return;
        showLoading();
        try {
            await authService.post(`/salesReturnApi/PostReturn?returnNo=${returnDocument.returnNo}`, {});
            showSnackbar(t('common.posted'), 'success');
        } catch (error: any) {
            showSnackbar(error.response?.data ?? error.message, 'error');
        } finally {
            hideLoading();
        }
    };

    const handleCancelReturn = async () => {
        if (preventIfUnsaved() || !returnDocument) return;
        const isConfirmed = window.confirm(t("dialog.areYouSure"));
        if (!isConfirmed) return;
        showLoading();
        try {
            await authService.post(`/salesReturnApi/CancelReturn?returnNo=${returnDocument.returnNo}`, {});
            showSnackbar(t('common.cancelled'), 'success');
            navigate({ path: "sales/return/list" });
        } catch (error: any) {
            showSnackbar(error.response?.data ?? error.message, 'error');
        } finally {
            hideLoading();
        }
    };

    const handleRefundReturn = async () => {
        if (preventIfUnsaved() || !returnDocument) return;
        showLoading();
        try {
            await authService.post(`/salesReturnApi/RefundReturn?returnNo=${returnDocument.returnNo}`, {});
            showSnackbar(t('common.refunded'), 'success');
        } catch (error: any) {
            showSnackbar(error.response?.data ?? error.message, 'error');
        } finally {
            hideLoading();
        }
    };

    const handleChange = async (key: keyof ReturnViewModel, value: any) => {
        let newDocument: ReturnViewModel | null = null;

        setReturnDocument((prev) => {
            if (!prev) return null;

            newDocument = { ...prev, [key]: value, hasChanges: true };
            return newDocument;
        });
    };

    const handleChanges = (changes: Partial<ReturnViewModel>) => {
        setReturnDocument((prev) => {
            if (!prev) return prev;
            return { ...prev, ...changes, hasChanges: true };
        });
    };

    const handleCancelChanges = () => {
        // showLoading();
        try {
            setReturnDocument((prev) => {
                if (!prev || !prev.originalValues) return prev;
                const original = prev.originalValues;
                const changes: Partial<ReturnViewModel> = {};
                for (const key in original) {
                    const typedKey = key as keyof ReturnViewModel;
                    const originalValue = original[typedKey];
                    const currentValue = prev[typedKey];
                    if (originalValue !== currentValue) {
                        (changes as any)[typedKey] = originalValue;
                    }
                }
                 if (Object.keys(changes).length === 0) return { ...prev, hasChanges: false };
                return { ...prev, ...changes, hasChanges: false };
            });
        } finally {
            //  hideLoading();
        }
    };






    const fetchReturn = async (no: string) => {
        showLoading();
        try {
            const response = await authService.get<ReturnViewModel>(`salesReturnApi/GetReturn?no=${returnNo}`);
            setReturnDocument(
                response
                    ? { ...response, originalValues: { ...response }, hasChanges: false }
                    : null
            );
            if (response) {
                //await fetchInvoiceLines(response.invoiceNo ?? "", invoiceQuery);
                //await fetchCreditLines(response.returnNo ?? "", creditQuery);
               // await fetchPayments(response.invoiceNo ?? "", paymentQuery);
                //await fetchRefundPayments(response.returnNo ?? "", refundPaymentQuery);
            }
        }
        catch (error: any) {
            showSnackbar(error.response?.data ?? error.message, 'error');
        } finally {
            setInitialLoad(false);
            hideLoading();
        }
    };

    const fetchInvoiceLines = async (invoiceNo: string, query: ListQuery) => {
        showLoading();
        try {
            const result = await authService.post<SalesLineDocumentResultModel>(
                `/salesReturnApi/GetInvoiceLines?invoiceNo=${invoiceNo}`,
                query
            );
            setInvoiceLines(result.items);
            setInvoiceTotalCount(result.count);
        }
        catch (error: any) {
            showSnackbar(error.response?.data ?? error.message, 'error');
        } finally {
            hideLoading();
        }
    };

    const fetchCreditLines = async (returnNo: string, query: ListQuery) => {
        showLoading();
        try {
            const result = await authService.post<SalesLineDocumentResultModel>(
                `/salesReturnApi/GetCreditLines?returnNo=${returnNo}&history=${history}`,
                query
            );
            setCreditLines(result.items);
            setCreditTotalCount(result.count);
        }
        catch (error: any) {
            showSnackbar(error.response?.data ?? error.message, 'error');
        } finally {
            hideLoading();
        }
    };

    const fetchPayments = async (invoiceNo: string, query: ListQuery) => {
        showLoading();
        try {
            const result = await authService.post<PaymentLineDocumentResultModel>(
                `/salesReturnApi/GetInvoicePayments?invoiceNo=${invoiceNo}`,
                query
            );
            setOriginalPayments(result.items);
            setPaymentTotalCount(result.count);
        }

        catch (error: any) {
            showSnackbar(error.response?.data ?? error.message, 'error');
        } finally {
            hideLoading();
        }
    };

    const balanceRefundPayments = async (returnNo: string) => {
        //showLoading();
        //try {
        //    const result = await authService.post(
        //        `/salesReturnApi/BalanceRefundPayments?returnNo=${returnNo}`,
        //        {}
        //    );
        //}
        //catch (error: any) {
        //    showSnackbar(error.response?.data ?? error.message, 'error');
        //} finally {
        //    hideLoading();
        //}
    };


    const fetchRefundPayments = async (returnNo: string, query: ListQuery) => {
        showLoading();
        try {
            const result = await authService.post<PaymentLineDocumentResultModel>(
                `/salesReturnApi/GetRefundPayments?returnNo=${returnNo}`,
                query
            );
            setRefundPayments(result.items);
            setRefundPaymentTotalCount(result.count);
        }
        catch (error: any) {
            showSnackbar(error.response?.data ?? error.message, 'error');
        } finally {
            hideLoading();
        }
    };

    const fetchReplacementOrder = async () => {
        if (!returnDocument?.returnNo) return;
        showLoading();
        try {
            const response = await authService.get<SalesDocumentViewModel>(`/salesReturnApi/GetReplacementOrder?rmaNumber=${returnDocument.returnNo}`);
            setReplacementOrder(response || null);
        }
        catch (error: any) {
            showSnackbar(error.response?.data ?? error.message, 'error');
        } finally {
            hideLoading();
        }
    };

    const createReplacementOrder = async () => {
        if (!returnDocument?.returnNo) return;
        if (history == true) return;
        showLoading();
        try {
            await authService.post(`/salesReturnApi/CreateReplacementOrder?returnNo=${returnDocument.returnNo}`, {});
            await fetchReplacementOrder();
            showSnackbar(t('common.saved'), 'success');
        } catch (error: any) {
            showSnackbar(error.response?.data ?? error.message, 'error');
        } finally {
            hideLoading();
        }
    };

    const CreateExchange = async () => {
        if (!returnDocument?.returnNo) return;
        if (history == true) return;
        showLoading();
        try {
            await authService.post(`/salesReturnApi/CreateExchange?returnNo=${returnDocument.returnNo}`, {});
            await fetchReplacementOrder();
            showSnackbar(t('common.saved'), 'success');
        } catch (error: any) {
            showSnackbar(error.response?.data ?? error.message, 'error');
        } finally {
            hideLoading();
        }
    };


    const handleReplacementLinesChanged = async () => {

        await fetchReplacementOrder();
        if (returnDocument?.returnNo) {
            await balanceRefundPayments(returnDocument.returnNo);
            await fetchRefundPayments(returnDocument.returnNo, refundPaymentQuery);
        }
    };

    const startAddLine = (line: SalesLineViewModel) => {
        setEditingInvoiceLine({
            returnNo: returnDocument?.returnNo,
            salesInvoiceLineId: line.id,
            quantity: line.quantity,
            refundAmount: line.amountIncludingVat,
            returnReason: "",
            returnType: 1,
            gUID: crypto.randomUUID(),
            description: line.description,
            itemNo: line.itemNo,
            variantId: line.variantId,
            colour: line.colourDescription,
            size: line.sizeDescription,
            length: line.lengthDescription,
            barcode: line.barcode,
            refundableAmount: (line.amountIncludingVat ?? 0) - (line.refundedAmount ?? 0),
            img_Url: line.img_Url,
            totalReturnableQuantity: line.quantity - (line.creditedQuantity ?? 0)
        });
    };

    const handleAddLineChange = (field: keyof ReturnModeRequest, value: any) => {
        setEditingInvoiceLine((prev) => {
            if (!prev) return prev;

            if (field === "quantity") {

                const max = prev.totalReturnableQuantity ?? 0;
                if (value > max) {
                    showSnackbar(
                        t("help.quantityExceedsReturnable", { max }),
                        "warning"
                    );
                    value = max;
                }
            }

            return { ...prev, [field]: value };
        });
    };

    const saveAddedLine = async () => {
        if (!editingInvoiceLine) return;
        showLoading();
        try {
            await authService.post(`/salesReturnApi/AddToReturn`, editingInvoiceLine);
            showSnackbar(t("common.saved"), "success");

            const newTotalCount = creditTotalCount + 1;
            const lastPage = Math.ceil(newTotalCount / creditQuery.pageSize) - 1;

            setCreditQuery((prev) => ({
                ...prev,
                Page: creditLines.length < creditQuery.pageSize ? prev.page : lastPage,
            }));
            setCreditTotalCount(newTotalCount);
            setEditingInvoiceLine(null);
            fetchRefundPayments(returnDocument?.returnNo ?? "", refundPaymentQuery);
        } catch (error: any) {
            showSnackbar(error.response?.data ?? error.message, "error");
        } finally {
            hideLoading();
        }
    };

    const startEditCreditLine = (line: SalesLineViewModel) => {
        const refundAmount = line.refundAmount ?? line.amountIncludingVat ?? 0;
        const originalAmount = line.amountIncludingVat ?? 0;
        const remainingAmount = originalAmount - refundAmount;

        setEditingCreditLine({
            ...line,
            remainingAmount,
        });
    };

    const handleCreditLineChange = (field: keyof SalesLineViewModel, value: any) => {
        if (editingCreditLine) {
            setEditingCreditLine((prev) => {
                if (!prev) return null;

                const updatedPropertiesChanged = prev.propertiesChanged ? [...prev.propertiesChanged] : [];
                const existingProperty = updatedPropertiesChanged.find((p) => p.key === field);

                if (existingProperty) {
                    existingProperty.propertyFetched = false;
                } else {
                    updatedPropertiesChanged.push({ key: field, propertyFetched: false });
                }

                let updatedLine: SalesLineViewModel = {
                    ...prev,
                    [field]: value,
                    propertiesChanged: updatedPropertiesChanged,
                };

                if (field === "refundAmount") {
                    const originalRefund = prev.refundAmount ?? prev.amountIncludingVat ?? 0;
                    const newRefund = value ?? 0;
                    const delta = newRefund - originalRefund;

                    updatedLine.remainingAmount = (prev.remainingAmount ?? 0) - delta;
                }

                return updatedLine;
            });
        }
    };

    const saveCreditLine = async () => {
        if (!editingCreditLine) return;
        showLoading();
        try {
            await authService.post<SalesLineViewModel>(`/salesReturnApi/EditCreditLine?returnNo=${returnDocument?.returnNo}`,
                editingCreditLine);
            showSnackbar(t("common.saved"), "success");
            await fetchCreditLines(returnDocument?.returnNo ?? "", creditQuery);
            setEditingCreditLine(null);
            fetchRefundPayments(returnDocument?.returnNo ?? "", refundPaymentQuery);
        } catch (error: any) {
            showSnackbar(error.response?.data ?? error.message, "error");
        } finally {
            hideLoading();
        }
    };

    const deleteCreditLine = async (line: SalesLineViewModel) => {
        showLoading();
        try {
            await authService.post(`/salesReturnApi/DeleteCreditLine?returnNo=${returnDocument?.returnNo}&salesLineId=${line.id}`, {});
            await fetchCreditLines(returnDocument?.returnNo ?? "", creditQuery);
            showSnackbar(t("common.deleted"), "success");
        } catch (error: any) {
            showSnackbar(error.response?.data ?? error.message, "error");
        } finally {
            hideLoading();
        }
    };




    const isInvoiceLineCreditable = (line: SalesLineViewModel): boolean => {
        const remainingQty = (line.quantity ?? 0) - (line.creditedQuantity ?? 0);
        if (remainingQty <= 0) return false;

        const creditedQty = creditLines
            .filter(cl => cl.salesInvoiceLineId === line.id)
            .reduce((sum, cl) => sum + (cl.quantity ?? 0), 0);

        return creditedQty < remainingQty;
    };

    const getMaxCreditableQuantity = (creditLine: SalesLineViewModel): number => {
        const invoice = invoiceLines.find(i => i.id === creditLine.salesInvoiceLineId);
        if (!invoice) return Infinity;

        const invoiceQty = invoice.quantity ?? 0;
        const creditedQty = invoice.creditedQuantity ?? 0;

        const alreadyCredited = creditLines
            .filter(cl => cl.salesInvoiceLineId === invoice.id && cl.id !== creditLine.id)
            .reduce((sum, cl) => sum + (cl.quantity ?? 0), 0);

        return Math.max(0, invoiceQty - creditedQty - alreadyCredited);
    };
    const maxQty = editingCreditLine ? getMaxCreditableQuantity(editingCreditLine) : Infinity;
    const qty = editingCreditLine?.quantity ?? 0;
    const quantityInvalid = qty > maxQty || qty <= 0;

    //history == "false", since js casts it to string via url parameter :()
    if (initialLoad) return(<></>)
    return (
        <>

            <CenteredCard
                ref={toolbarRef}
                title={t("common.editReturn")}
                isLoading={isLoading}
                handleSave={handleSave}
                handleCancel={handleCancelChanges}
                hasChanges={hasPendingChanges()}
            >
                <Box sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    maxWidth: "fit-content",
                    mb: 2
                }}>

                    {history == false && (
                        <SalesDocumentActions
                            onPost={history == false ? handlePostReturn : undefined}
                            onCancel={history == false ? handleCancelReturn : undefined}
                            onRefund={history == false ? handleRefundReturn : undefined}
                            onSendEmail={undefined}
                            onIntegrate={undefined}
                            onPrint={undefined}

                            customActions={[
                                ...((history == false)
                                    ? [
                                        { label: t("common.createExchange"), onClick: () => CreateExchange() }
                                    ]
                                    : []),
                            ]}
                     
                        />
                    )}

                    {showReplacement && replacementOrder == null && history == false && (
                        <Button variant="contained" onClick={createReplacementOrder} sx={{ mb: 2 }}>
                            {t("common.createReplacementOrder")}
                        </Button>
                    )}
                </Box>
                {isLoading && <Typography>Loading...</Typography>}
                {/* Button to open SalesDocumentLines modal */}

                <Grid container spacing={2} >
                    <Grid item xs={12} md={12}>
                        <TextField
                            label={t("common.returnNo")}
                            value={returnDocument?.returnNo}
                            fullWidth
                            disabled={true}
                            margin="normal"
                            InputLabelProps={{ shrink: true }}
                        />


                    </Grid>


                    <Grid item xs={12} md={12}>
                        <Box
                            component="a"
                            href={`/sales/history/document?documentNo=${returnDocument?.invoiceNo}&documentType=2`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ textDecoration: "none", color: "inherit", display: "block" }}
                        >
                            <TextField
                                label={t("common.invoiceNo")}
                                value={returnDocument?.invoiceNo}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                margin="normal"
                                InputProps={{ readOnly: true }}
                                sx={{
                                    "& .MuiInputBase-input": {
                                        textDecoration: "underline",
                                        color: "blue",
                                        cursor: "pointer",
                                    },
                                }}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Box
                            component="a"
                            href={`/sales/history/document?documentNo=${returnDocument?.orderNo}&documentType=1`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ textDecoration: "none", color: "inherit", display: "block" }}
                        >
                            <TextField
                                label={t("common.orderNo")}
                                value={returnDocument?.orderNo}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                margin="normal"
                                InputProps={{ readOnly: true }}
                                sx={{
                                    "& .MuiInputBase-input": {
                                        textDecoration: "underline",
                                        color: "blue",
                                        cursor: "pointer",
                                    },
                                }}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <TextField
                            label={t("common.customerNo")}
                            value={returnDocument?.returnCustomer}
                            fullWidth
                            disabled={true}
                            margin="normal"
                            InputLabelProps={{ shrink: true }}
                        />


                    </Grid>


                    <Grid item xs={12} >

                        <TextField
                            label={t("common.orderDate")}
                            type="date"
                            value={returnDocument?.orderDate?.split("T")[0]}

                            fullWidth
                            margin="normal"
                            InputLabelProps={{ shrink: true }}
                            disabled={true}
                        />

                    </Grid>
                    <Grid item xs={12} >

                        <TextField
                            label={t("common.shipmentDate")}
                            type="date"
                            value={returnDocument?.shipmentDate?.split("T")[0]}

                            fullWidth
                            margin="normal"
                            InputLabelProps={{ shrink: true }}
                            disabled={true}

                        />
                    </Grid>
                    {/* Switch for Shipping Information */}


                    <Grid item xs={12} >
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showShippingInfo}
                                    onChange={(e) => setShowShippingInfo(e.target.checked)}
                                />
                            }
                            label={t("common.showShippingInfo")}
                        />
                    </Grid>
                    {/* Shipping Information Section */}
                    {showShippingInfo && (
                        <>
                            <Box
                                sx={{
                                    backgroundColor: "secondary.main",
                                    padding: 2,
                                    borderRadius: 2,
                                }}
                            >

                                <Grid item xs={12} >
                                    <TextField
                                        label={t("common.shipToName")}
                                        value={returnDocument?.returnName}
                                        onChange={(e) => handleChange("returnName", e.target.value)}
                                        fullWidth
                                        margin="normal"
                                        disabled={history == true}
                                        inputProps={{ maxLength: 100 }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <InfoButton infoText={t("help.shipToNameHelp")} />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <TextField
                                        label={t("common.shipToName2")}
                                        value={returnDocument?.returnName2}
                                        onChange={(e) => handleChange("returnName2", e.target.value)}
                                        fullWidth
                                        margin="normal"
                                        disabled={history == true}
                                        inputProps={{ maxLength: 100 }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <InfoButton infoText={t("help.shipToName2Help")} />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <TextField
                                        label={t("common.shipToAddress")}
                                        value={returnDocument?.returnAdress}
                                        onChange={(e) => handleChange("returnAdress", e.target.value)}
                                        fullWidth
                                        margin="normal"
                                        disabled={history == true}
                                        inputProps={{ maxLength: 100 }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <InfoButton infoText={t("help.shipToAddressHelp")} />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />

                                    <TextField
                                        label={t("common.shipToAddress2")}
                                        value={returnDocument?.returnAdress2}
                                        onChange={(e) => handleChange("returnAdress2", e.target.value)}
                                        fullWidth
                                        margin="normal"
                                        disabled={history == true}
                                        inputProps={{ maxLength: 100 }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <InfoButton infoText={t("help.shipToAddress2Help")} />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />

                                    <TextField
                                        label={t("common.shipToMobile")}
                                        value={returnDocument?.returnTelephone}
                                        onChange={(e) => handleChange("returnTelephone", e.target.value)}
                                        fullWidth
                                        margin="normal"
                                        disabled={history == true}
                                        inputProps={{ maxLength: 100 }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <InfoButton infoText={t("help.shipToMobileHelp")} />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <TextField
                                        label={t("common.shipToEmail")}
                                        value={returnDocument?.returnEmail}
                                        onChange={(e) => handleChange("returnEmail", e.target.value)}
                                        fullWidth
                                        margin="normal"
                                        disabled={history == true}
                                        inputProps={{ maxLength: 100 }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <InfoButton infoText={t("help.shipToEmailHelp")} />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} >
                                    <TextField
                                        label={t("common.shipToCounty")}
                                        value={returnDocument?.returnRegion}
                                        onChange={(e) => handleChange("returnRegion", e.target.value)}
                                        fullWidth
                                        margin="normal"
                                        disabled={history == true}
                                        inputProps={{ maxLength: 100 }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <InfoButton infoText={t("help.shipToCountyHelp")} />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <TextField
                                        label={t("common.shipToPostCode")}
                                        value={returnDocument?.returnPostCode}
                                        onChange={(e) => handleChange("returnPostCode", e.target.value)}
                                        fullWidth
                                        margin="normal"
                                        disabled={history == true}
                                        inputProps={{ maxLength: 50 }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <InfoButton infoText={t("help.shipToPostCodeHelp")} />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <TextField
                                        label={t("common.shipToCity")}
                                        value={returnDocument?.returnCity}
                                        onChange={(e) => handleChange("returnCity", e.target.value)}
                                        fullWidth
                                        margin="normal"
                                        disabled={history == true}
                                        inputProps={{ maxLength: 100 }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <InfoButton infoText={t("help.shipToCityHelp")} />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <FormControl fullWidth margin="normal">
                                        <CountryAutoComplete
                                            key={returnDocument?.returnCountry || null}
                                            valueId={returnDocument?.returnCountry || null}
                                            onChange={(selected) => handleChange("returnCountry", selected?.value || "")}
                                            label={t("common.shipToCountry")}
                                            disabled={history == true}
                                            infoText={t("help.shipToCountryHelp")}
                                        />

                                    </FormControl>

                                </Grid>
                            </Box>
                        </>
                    )}

                    <Grid item xs={12} >


                        <FormControl fullWidth margin="normal">
                            <LocationAutoComplete
                                key={returnDocument?.locationCode || null}
                                valueId={returnDocument?.locationCode || null}
                                onChange={(selected) => handleChange("locationCode", selected?.value || "")}
                                disabled={history == true}
                                infoText={t("help.locationHelp")}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <ShippingAgentServiceSelect
                            key={`${returnDocument?.shippingAgentCode ?? ""}::${returnDocument?.shippingServiceCode ?? ""}`}
                            shippingAgentCode={returnDocument?.shippingAgentCode}
                            shippingServiceCode={returnDocument?.shippingServiceCode}
                            onChange={(agent, service) =>
                                handleChanges({
                                    shippingAgentCode: agent,
                                    shippingServiceCode: service,
                                })
                            }
                            infoTextAgent={t("help.shippingAgentHelp")}
                            infoTextService={t("help.shippingServiceHelp")}
                            disabled={history == true}
                            isLoading={isLoading}
                        />
                    </Grid>




                    <Box sx={{
                        padding: "16px", width: "98vw",
                        borderRadius: 2
                    }}>
                        <Grid item xs={12}>
                            <Box sx={{ backgroundColor: "secondary.main", padding: 3, borderRadius: 2 }}>
                                <Typography variant="h6" sx={{ mb: 1 }}>{t("common.refundPayments")}</Typography>

                                <>
                                    <Grid container spacing={2} sx={{ mb: 2 }}>
                                        {refundPayments.map((payment, index) => (
                                            <Grid item xs={12} key={index}>
                                                <Box
                                                    sx={{
                                                        backgroundColor: "background.paper",
                                                        padding: 2,
                                                        borderRadius: 2,
                                                        boxShadow: 2,
                                                    }}
                                                >
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={12} sm={4}>
                                                            <TextField
                                                                label={t("common.paymentMethod")}
                                                                value={payment.paymentMethodDescription ?? ""}
                                                                disabled
                                                                fullWidth
                                                                margin="dense"
                                                                variant="outlined"
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={4}>
                                                            <TextField
                                                                label={t("common.refundAmount")}
                                                                value={history ? payment.refundedAmount : (payment.refundAmount ?? ((payment.amount ?? 0) - (payment.refundedAmount ?? 0)))}
                                                                disabled
                                                                fullWidth
                                                                margin="dense"
                                                                variant="outlined"
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={4}>
                                                            <TextField
                                                                label={t("common.currency")}
                                                                value={payment.currencyCode ?? ""}
                                                                disabled
                                                                fullWidth
                                                                margin="dense"
                                                                variant="outlined"
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            </Grid>
                                        ))}

                                    </Grid>
                                    {refundPayments?.length === 0 && (
                                        <Typography align="center">{t("common.noEntriesFound")}</Typography>
                                    )}
                                </>

                            </Box>
                        </Grid>
                        <ResponsivePagination
                            page={refundPaymentQuery.page}
                            pageSize={refundPaymentQuery.pageSize}
                            totalCount={refundPaymentTotalCount}
                            onPageChange={(newPage) => setRefundPaymentQuery(prev => ({ ...prev, page: newPage }))}
                            sx={{ marginTop: "1rem" }}
                        />
                    </Box>

                    <FormControlLabel
                        control={<Switch checked={showInvoicePayments} onChange={(_, val) => setShowInvoicePayments(val)} />}
                        label={t("common.showInvoicePayments")}
                    />
                    {showInvoicePayments && (
                        <Box sx={{
                            padding: "16px", width: "98vw",
                            borderRadius: 2
                        }}>
                            <Grid item xs={12}>
                                <Box sx={{ backgroundColor: "secondary.main", padding: 3, borderRadius: 2 }}>


                                    <Grid container spacing={2} sx={{ mb: 2 }}>
                                        {originalPayments.map((payment, index) => (
                                            <Grid item xs={12} key={index}>
                                                <Box
                                                    sx={{
                                                        backgroundColor: "background.paper",
                                                        padding: 2,
                                                        borderRadius: 2,
                                                        boxShadow: 2,
                                                    }}
                                                >
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={12} sm={4}>
                                                            <TextField
                                                                label={t("common.paymentMethod")}
                                                                value={payment.paymentMethodDescription ?? ""}
                                                                disabled
                                                                fullWidth
                                                                margin="dense"
                                                                variant="outlined"

                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={4}>
                                                            <TextField
                                                                label={t("common.amount")}
                                                                value={payment.amount ?? 0}
                                                                disabled
                                                                fullWidth
                                                                margin="dense"
                                                                variant="outlined"
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={4}>
                                                            <TextField
                                                                label={t("common.refundedAmount")}
                                                                value={payment.refundedAmount ?? 0}
                                                                disabled
                                                                fullWidth
                                                                margin="dense"
                                                                variant="outlined"
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={4}>
                                                            <TextField
                                                                label={t("common.remainingAmount")}
                                                                value={(payment.amount ?? 0) - (payment.refundedAmount ?? 0)}
                                                                disabled
                                                                fullWidth
                                                                margin="dense"
                                                                variant="outlined"
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={4}>
                                                            <TextField
                                                                label={t("common.currency")}
                                                                value={payment.currencyCode ?? ""}
                                                                disabled
                                                                fullWidth
                                                                margin="dense"
                                                                variant="outlined"
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                    {originalPayments?.length === 0 && (
                                        <Typography align="center">{t("common.noEntriesFound")}</Typography>
                                    )}
                                </Box>
                            </Grid>
                            <ResponsivePagination
                                page={paymentQuery.page}
                                pageSize={paymentQuery.pageSize}
                                totalCount={paymentTotalCount}
                                onPageChange={(newPage) => setPaymentQuery(prev => ({ ...prev, page: newPage }))}
                                sx={{ marginTop: "1rem" }}
                            />
                        </Box>
                    )}
                    <FormControlLabel
                        control={<Switch checked={showReplacement} onChange={(_, val) => setShowReplacement(val)} />}
                        label={t("common.showReplacement")}
                    />
                    {showReplacement && replacementOrder == null && history == false && (
                        <Button variant="contained" onClick={createReplacementOrder} sx={{ mb: 2 }}>
                            {t("common.createReplacementOrder")}
                        </Button>
                    )}
                    {showReplacement && replacementOrder && (
                        <>
                            <Button variant="contained" onClick={() => setReplacementDialogOpen(true)} sx={{ mb: 2 }}>
                                {t("common.openReplacementOrder")}
                            </Button>
                            <Dialog
                                open={replacementDialogOpen}
                                onClose={() => setReplacementDialogOpen(false)}
                                maxWidth={false}    
                                fullWidth
                                sx={{
                                    '& .MuiDialog-paper': {
                                        margin: 0,                 
                                        borderRadius: 0,           
                                        boxShadow: 'none',      
                                        background: 'transparent', 
                                        width: '100%',            
                                        maxWidth: '100%',           
                                    },
                                }}
                            >
                                <DialogContent
                                    sx={{
                                        p: 0,                      
                                        m: 0,
                                        width: '100%',
                                        height: '100%',
                                        background: 'transparent',  
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <SalesDocumentHeader
                                        title={t("common.replacementOrder")}
                                        documentNo={replacementOrder.no}
                                        documentType={replacementOrder.documentType}
                                        readUrl={replacementOrder.xOrderStatus >= 4 ? `/salesDocumentApi/GetDocument?documentNo=${replacementOrder.no}&documentType=1&open=false` : `/orderApi/GetOrder?documentNo=${replacementOrder.no}`}
                                        updateUrl="/orderApi/UpdateOrder"
                                        deleteUrl="NAN"
                                        history={replacementOrder.xOrderStatus >= 4}
                                        onLinesChanged={handleReplacementLinesChanged}
                                        isDialog={true}
                                    />
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={() => setReplacementDialogOpen(false)}>{t("common.cancel")}</Button>

                                </DialogActions>
                            </Dialog>
                        </>
                    )}

                    {showReplacement && !replacementOrder && (
                                <Box sx={{
                        width: "98vw",
                     
                    }}>
                        <Typography align="center">{t("common.noEntriesFound")}</Typography>
                        </Box>
                    )}
                    <Box sx={{
                        padding: "16px", width: "98vw",
                        borderRadius: 2
                    }}>
                        <Grid item xs={12}>
                            <Box sx={{ backgroundColor: "secondary.main", padding: 3, borderRadius: 2 }}>


                                <Typography variant="h6" sx={{ mb: 1 }}>{t("common.invoiceLines")}</Typography>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                    onClick={toggleInvoiceRowMode}
                                    sx={{ marginBottom: 1 }}
                                >
                                    {t("common.toggleListMode")}
                                </Button>
                                {invoiceRowMode ? (
                                    <Grid container spacing={1}>
                                        {invoiceLines.map((line, index) => (
                                            <Grid item xs={12} key={line.id}>
                                                <Grid
                                                    container
                                                    spacing={1}
                                                    sx={{
                                                        backgroundColor: index % 2 === 0 ? "transparent" : "#fafafa",
                                                        ":hover": {
                                                            backgroundColor: index % 2 === 0 ? "#f0f0f0" : "#e8e8e8",
                                                        },
                                                    }}
                                                >
                                                    <Grid item xs={12} lg={3} sx={{ display: "flex", justifyContent: "center" }}>
                                                        <Box sx={{ width: "100%", maxWidth: "80px", maxHeight: "80px", height: "auto", borderRadius: "8px" }}>
                                                            <ImageBox imageUrl={line.img_Url ?? ""} altText={line.description || "No Description"} fallbackText={t("common.noImage")} width="80px" height="80px" />
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs={12} lg={5}>
                                                        <Typography variant="body2" sx={{ color: "gray" }}>{line.itemNo ?? line.accountNo}</Typography>
                                                        <Typography variant="body1">{line.description}</Typography>
                                                        <Typography variant="body2">{generateVariantLabel(line)}</Typography>
                                                    </Grid>
                                                    <Grid item xs={12} lg={3}>
                                                        <Typography variant="body2" sx={{ color: "gray" }}>{t("common.qty")}: {formatNumber(line.quantity)}</Typography>
                                                        <Typography variant="body1">{t("common.amount")}: {formatNumber(line.amountIncludingVat)}</Typography>
                                                    </Grid>
                                                    <Grid item xs={12} lg={1} sx={{ display: "flex", justifyContent: "flex-end" }}>
                                                        {isInvoiceLineCreditable(line) && !history && (
                                                            <Tooltip title={t("common.add")}>
                                                                <IconButton size="small" color="inherit" onClick={() => startAddLine(line)}>
                                                                    <AddIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        ))}
                                    </Grid>
                                ) : (
                                    <Grid container spacing={2} sx={{ mb: 2 }} justifyContent="center" alignItems="center">
                                        {invoiceLines.map((line) => {
                                            return (
                                                <Grid item sm={invoiceLines.length === 1 ? 12 : 6} md={invoiceLines.length === 1 ? 12 : 6} key={line.id}>
                                                    <Card variant="outlined">
                                                        {isInvoiceLineCreditable(line) && !history && (
                                                            <Toolbar sx={{ backgroundColor: "primary.light", color: "white" }}>
                                                                <Tooltip title={t("common.add")}>
                                                                    <IconButton size="small" color="inherit" onClick={() => startAddLine(line)}>
                                                                        <AddIcon />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Toolbar>
                                                        )}
                                                        <CardContent>
                                                            <Grid container spacing={1}>
                                                                <Grid item xs={12} sx={{ marginBottom: "1rem" }}>
                                                                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                                                                        <Box sx={{ width: "100%", maxWidth: "120px", maxHeight: "120px", height: "auto", borderRadius: "8px" }}>
                                                                            <ImageBox imageUrl={line.img_Url ?? ""} altText={line.description || "No Description"} fallbackText={t("common.noImage")} width="120px" height="120px" />
                                                                        </Box>
                                                                    </Box>
                                                                </Grid>
                                                                <Grid item xs={12}>
                                                                    <Typography variant="h6" fontWeight="bold">{line.itemNo ?? line.accountNo}</Typography>
                                                                </Grid>
                                                                <Grid item xs={12}>
                                                                    <Typography>{line.description}</Typography>
                                                                </Grid>
                                                                <Grid item xs={12}>
                                                                    <Typography variant="body2" sx={{ color: "gray" }}>{generateVariantLabel(line)}</Typography>
                                                                </Grid>
                                                                <Grid item xs={6}>
                                                                    <Typography variant="body2">{t("common.quantity")}:</Typography>
                                                                    <Typography variant="body1">{formatNumber(line.quantity)}</Typography>
                                                                </Grid>
                                                                <Grid item xs={6}>
                                                                    <Typography variant="body2">{t("common.creditedQuantity")}:</Typography>
                                                                    <Typography variant="body1">{formatNumber(line.creditedQuantity ?? 0)}</Typography>
                                                                </Grid>
                                                                <Grid item xs={6}>
                                                                    <Typography variant="body2">{t("common.refundedAmount")}:</Typography>
                                                                    <Typography variant="body1">{formatNumber(line.refundedAmount ?? 0)}</Typography>
                                                                </Grid>
                                                                <Grid item xs={6}>
                                                                    <Typography variant="body2">{t("common.amount")}:</Typography>
                                                                    <Typography variant="body1">{formatNumber(line.amountIncludingVat)}</Typography>
                                                                </Grid>
                                                            </Grid>
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                )}


                            </Box>
                            <ResponsivePagination
                                page={invoiceQuery.page}
                                pageSize={invoiceQuery.pageSize}
                                totalCount={invoiceTotalCount}
                                onPageChange={(newPage) => setInvoiceQuery(prev => ({ ...prev, page: newPage }))}
                                sx={{ marginTop: "1rem" }}
                            />
                        </Grid>


                        <Grid item xs={12} sx={{ mt: 4 }}>
                            <Box sx={{ backgroundColor: "secondary.main", padding: 3, borderRadius: 2, marginTop: 2 }}>

                                <Typography variant="h6" sx={{ mb: 1 }}>{t("common.creditLines")}</Typography>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                    onClick={toggleCreditRowMode}
                                    sx={{ marginBottom: 1 }}
                                >
                                    {t("common.toggleListMode")}
                                </Button>
                                {creditRowMode ? (
                                    <Grid container spacing={1}>
                                        {creditLines.map((line, index) => (
                                            <Grid item xs={12} key={line.id}>
                                                <Grid
                                                    container
                                                    spacing={1}
                                                    sx={{
                                                        backgroundColor: index % 2 === 0 ? "transparent" : "#fafafa",
                                                        ":hover": { backgroundColor: index % 2 === 0 ? "#f0f0f0" : "#e8e8e8" },
                                                    }}
                                                >
                                                    <Grid item xs={12} lg={3} sx={{ display: "flex", justifyContent: "center" }}>
                                                        <Box sx={{ width: "100%", maxWidth: "80px", maxHeight: "80px", height: "auto", borderRadius: "8px" }}>
                                                            <ImageBox imageUrl={line.img_Url ?? ""} altText={line.description || "No Description"} fallbackText={t("common.noImage")} width="80px" height="80px" />
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs={12} lg={4}>
                                                        <Typography variant="body2" sx={{ color: "gray" }}>{line.itemNo ?? line.accountNo}</Typography>
                                                        <Typography variant="body1">{line.description}</Typography>
                                                        <Typography variant="body2">{generateVariantLabel(line)}</Typography>
                                                    </Grid>
                                                    <Grid item xs={12} lg={3}>
                                                        <Typography variant="body2" sx={{ color: "gray" }}>{t("common.qty")}: {formatNumber(line.quantity)}</Typography>
                                                        <Typography variant="body1">{t("common.amount")}: {formatNumber(line.amountIncludingVat)}</Typography>
                                                    </Grid>
                                                    {!history && (
                                                        <>
                                                            <Grid item xs={12} lg={1} sx={{ display: "flex", justifyContent: "flex-end" }}>
                                                                <Tooltip title={t("common.edit")}><IconButton color="inherit" onClick={() => startEditCreditLine(line)}><EditIcon /></IconButton></Tooltip>
                                                            </Grid>
                                                            <Grid item xs={12} lg={1} sx={{ display: "flex", justifyContent: "flex-end" }}>
                                                                <Tooltip title={t("common.delete")}><IconButton color="error" onClick={() => deleteCreditLine(line)}><DeleteIcon /></IconButton></Tooltip>
                                                            </Grid>
                                                        </>
                                                    )}
                                                </Grid>
                                            </Grid>
                                        ))}
                                    </Grid>
                                ) : (
                                    <Grid container spacing={2} sx={{ mb: 2 }} justifyContent="center" alignItems="center">
                                        {creditLines.map((line) => (
                                            <Grid item sm={creditLines.length === 1 ? 12 : 6} md={creditLines.length === 1 ? 12 : 6} key={line.id}>
                                                <Card variant="outlined">
                                                    {!history && (
                                                        <Toolbar sx={{ backgroundColor: "primary.light", color: "white" }}>
                                                            <Grid container spacing={1}>
                                                                <Grid item xs={4} >
                                                                    <Typography variant="h6" fontWeight="bold">
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item xs={4} >
                                                                    <Tooltip title={t("common.edit")}><IconButton color="inherit" onClick={() => startEditCreditLine(line)}><EditIcon /></IconButton></Tooltip>
                                                                </Grid>
                                                                <Grid item xs={4} >
                                                                    <Tooltip title={t("common.delete")}><IconButton color="error" onClick={() => deleteCreditLine(line)}><DeleteIcon /></IconButton></Tooltip>
                                                                </Grid>
                                                            </Grid>
                                                        </Toolbar>
                                                    )}
                                                    <CardContent>
                                                        <Grid container spacing={1}>
                                                            <Grid item xs={12} sx={{ marginBottom: "1rem" }}>
                                                                <Box sx={{ display: "flex", justifyContent: "center" }}>
                                                                    <Box sx={{ width: "100%", maxWidth: "120px", maxHeight: "120px", height: "auto", borderRadius: "8px" }}>
                                                                        <ImageBox imageUrl={line.img_Url ?? ""} altText={line.description || "No Description"} fallbackText={t("common.noImage")} width="120px" height="120px" />
                                                                    </Box>
                                                                </Box>
                                                            </Grid>
                                                            <Grid item xs={12}>
                                                                <Typography variant="h6" fontWeight="bold">{line.itemNo ?? line.accountNo}</Typography>
                                                            </Grid>
                                                            <Grid item xs={12}>
                                                                <Typography>{line.description}</Typography>
                                                            </Grid>
                                                            <Grid item xs={12}>
                                                                <Typography variant="body2" sx={{ color: "gray" }}>{generateVariantLabel(line)}</Typography>
                                                            </Grid>
                                                            <Grid item xs={6}>
                                                                <Typography variant="body2">{t("common.quantity")}:</Typography>
                                                                <Typography variant="body1">{formatNumber(line.quantity)}</Typography>
                                                            </Grid>
                                                            <Grid item xs={6}>
                                                                <Typography variant="body2">{t("common.refundAmount")}:</Typography>
                                                                <Typography variant="body1">{formatNumber(line?.refundAmount ?? line?.amountIncludingVat ?? 0)}</Typography>
                                                            </Grid>
                                                            <Grid item xs={12}>
                                                                <Box sx={{ backgroundColor: "lightgreen", padding: "0.5rem", borderRadius: "4px", textAlign: "center" }}>
                                                                    <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                                                                        {t("common.amount")}: {formatNumber(line.amountIncludingVat)}
                                                                    </Typography>
                                                                </Box>
                                                            </Grid>
                                                        </Grid>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        ))}
                                    </Grid>
                                )}
                                <ResponsivePagination
                                    page={creditQuery.page}
                                    pageSize={creditQuery.pageSize}
                                    totalCount={creditTotalCount}
                                    onPageChange={(newPage) => setCreditQuery(prev => ({ ...prev, page: newPage }))}
                                    sx={{ marginTop: "1rem" }}
                                />
                            </Box>
                        </Grid>
                    </Box>

                    <Dialog open={!!editingInvoiceLine} onClose={() => setEditingInvoiceLine(null)} >
                        <Box sx={{ width: "100%", p: 3 }}>
                            <Typography variant="h6" gutterBottom>{t('actions.addToReturn')}</Typography>
                            <Box sx={{ display: "flex", justifyContent: "center" }}>


                                <Box sx={{ width: "100%", maxWidth: "120px", maxHeight: "120px", height: "auto", borderRadius: "8px" }}>
                                    <ImageBox
                                        imageUrl={editingInvoiceLine?.img_Url ?? ""}
                                        altText={editingInvoiceLine?.description || "No Description"}
                                        fallbackText={t("common.noImage")}
                                        width="120px"
                                        height="120px"

                                    />
                                </Box>
                            </Box>
                            <TextField fullWidth margin="normal" label={t('common.itemNo')} value={editingInvoiceLine?.itemNo ?? ''} disabled={true} />
                            <TextField fullWidth margin="normal" label={t('common.description')} value={editingInvoiceLine?.description ?? ''} disabled={true} />
                            {editingInvoiceLine?.variantId && (< TextField fullWidth margin="normal" label={t('common.variant')} value={generateVariantLabelFromReturnMode(editingInvoiceLine)} disabled={true} />)}


                            <LocalizedNumberInputs
                                label={t("common.quantity")}
                                value={editingInvoiceLine?.quantity ?? 1}
                                onValueChange={(value) => {
                                    handleAddLineChange("quantity", value ?? null);
                                }}
                                required
                                error={editingInvoiceLine?.quantity == null}
                                helperText={editingInvoiceLine?.quantity == null ? t("help.quantityRequired") : ""}
                                allowNegative={false} // Prevents negative numbers
                                decimalScale={4} // Ensures max 4 decimals
                                thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                                decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}

                            />
                            <ReturnReasonAutoComplete
                                key={editingInvoiceLine?.returnReason ?? null}
                                valueId={editingInvoiceLine?.returnReason ?? null}
                                onChange={(option) => handleAddLineChange('returnReason', option?.value || '')}
                                label={t('common.returnReason')}
                            />
                            <LocalizedNumberInputs
                                label={t("common.refundAmount")}
                                value={editingInvoiceLine?.refundAmount ?? 0}
                                onValueChange={(value) => {
                                    handleAddLineChange("refundAmount", value ?? null);
                                }}
                                required
                                error={editingInvoiceLine?.refundAmount == null}
                                helperText={editingInvoiceLine?.refundAmount == null ? t("help.amountRequired") : ""}
                                allowNegative={false} // Prevents negative numbers
                                decimalScale={4} // Ensures max 4 decimals
                                thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                                decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}

                            />
                            <LocalizedNumberInputs
                                label={t("common.remainingAmount")}
                                value={((editingInvoiceLine?.refundableAmount ?? 0) - (editingInvoiceLine?.refundAmount ?? 0))}
                                onValueChange={(value) => {

                                }}
                                disabled={true}
                                allowNegative={true} // Prevents negative numbers
                                decimalScale={4} // Ensures max 4 decimals

                            />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                                <Button variant="contained" startIcon={<SaveIcon />} onClick={saveAddedLine}>{t('common.add')}</Button>
                                <Button variant="outlined" startIcon={<CancelIcon />} onClick={() => setEditingInvoiceLine(null)}>{t('common.cancel')}</Button>
                            </Box>
                        </Box>
                    </Dialog>

                    <Dialog open={!!editingCreditLine} onClose={() => setEditingCreditLine(null)}>
                        <Box sx={{ width: "100%", p: 3 }}>
                            <Typography variant="h6" gutterBottom>{t('common.edit')}</Typography>
                            <Box sx={{ display: "flex", justifyContent: "center" }}>
                                <Box sx={{ width: "100%", maxWidth: "120px", maxHeight: "120px", height: "auto", borderRadius: "8px" }}>
                                    <ImageBox
                                        imageUrl={editingCreditLine?.img_Url ?? ""}
                                        altText={editingCreditLine?.description || "No Description"}
                                        fallbackText={t("common.noImage")}
                                        width="120px"
                                        height="120px"
                                    />
                                </Box>
                            </Box>

                            <TextField fullWidth margin="normal" label={t('common.itemNo')} value={editingCreditLine?.itemNo ?? ''} disabled />
                            <TextField fullWidth margin="normal" label={t('common.description')} value={editingCreditLine?.description ?? ''} disabled />
                            {editingCreditLine?.variantId && (
                                <TextField
                                    fullWidth
                                    margin="normal"
                                    label={t('common.variant')}
                                    value={generateVariantLabelFromReturnMode(editingCreditLine)}
                                    disabled
                                />
                            )}

                            <LocalizedNumberInputs
                                label={t("common.quantity")}
                                value={qty}
                                onValueChange={(value) => handleCreditLineChange("quantity", value ?? null)}
                                required
                                error={quantityInvalid}
                                helperText={
                                    quantityInvalid
                                        ? t("help.invalidQuantity", { max: maxQty }) // "Max allowed: {max}" — localize as needed
                                        : ""
                                }
                                allowNegative={false}
                                decimalScale={4}
                                thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                                decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}
                            />

                            <ReturnReasonAutoComplete
                                key={editingCreditLine?.returnReason ?? null}
                                valueId={editingCreditLine?.returnReason ?? null}
                                onChange={(option) => handleCreditLineChange("returnReason", option?.value || "")}
                                label={t("common.returnReason")}
                            />

                            <LocalizedNumberInputs
                                label={t("common.refundAmount")}
                                value={editingCreditLine?.refundAmount ?? editingCreditLine?.amountIncludingVat ?? 0}
                                onValueChange={(value) => handleCreditLineChange("refundAmount", value ?? null)}
                                required
                                error={(editingCreditLine?.refundAmount ?? editingCreditLine?.amountIncludingVat) == null}
                                helperText={(editingCreditLine?.refundAmount ?? editingCreditLine?.amountIncludingVat) == null ? t("help.amountRequired") : ""}
                                allowNegative={false}
                                decimalScale={4}
                                thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                                decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}
                            />

                            <LocalizedNumberInputs
                                label={t("common.remainingAmount")}
                                value={
                                    editingCreditLine?.remainingAmount ?? 0

                                }
                                onValueChange={() => { }}
                                disabled
                                allowNegative
                                decimalScale={4}
                            />

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                                <Button variant="contained" startIcon={<SaveIcon />} onClick={saveCreditLine}>{t('common.save')}</Button>
                                <Button variant="outlined" startIcon={<CancelIcon />} onClick={() => setEditingCreditLine(null)}>{t('common.cancel')}</Button>
                            </Box>
                        </Box>
                    </Dialog>
                </Grid>


                <Box sx={{ marginTop: 2, textAlign: "center" }}>
                    <Button variant="contained" onClick={handleSave} disabled={isLoading || history} sx={{ marginRight: 2 }}>
                        {t("common.save")}
                    </Button>
                    <Button variant="outlined" onClick={goBack}>
                        {t("common.back")}
                    </Button>
                </Box>
            </CenteredCard>
        </>
    );
};

export default ReturnEditPage;